import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store/store';
import { updateObjective } from '../../store/map3PendingSlice';
import { Field } from '../../components_catalyst/fieldset';
import { Textarea } from '../../components_catalyst/textarea';
import { Button } from '../../components_catalyst/button';
import { Comment1 } from '../../types';
import { Input } from '../../components_catalyst/input';
import { getShortIdFromUuid } from '@mm-shared/utils/getShortIdFromUuid';
import { useCurrentMap } from '../../hooks/useCurrentMap';
// import { selectUserRoleForMap, selectUserProfile } from '../../store/selectors';
import { selectUserProfile } from '../../store/selectors';
import { useRoleWithOverride } from '../../hooks/useRoleWithOverride';
import { getPermissionsForRole } from '../../utils/getPermissionsForRole';

interface AddEditComment1Props {
  objectiveUuid: string;
  forIntervalEnding: string;
  commentToEdit?: Comment1;
  onSave?: () => void;
  onCancel?: () => void;
}

export function AddEditComment1({
  objectiveUuid,
  forIntervalEnding,
  commentToEdit,
  onSave,
  onCancel,
}: AddEditComment1Props) {
  const [commentText, setCommentText] = useState('');
  const [showVideoInput, setShowVideoInput] = useState(false);
  const [videoUrl, setVideoUrl] = useState('');
  const [videoUrlError, setVideoUrlError] = useState('');
  const [commentTextError, setCommentTextError] = useState('');
  const dispatch = useDispatch<AppDispatch>();

  const objective = useSelector((state: RootState) =>
    state.map3Data.objectives.find((obj) => obj.uuid === objectiveUuid),
  );
  const userProfile = useSelector((state: RootState) => selectUserProfile(state));

  // Check user's role for comment permissions
  const currentMap = useCurrentMap();
  // const userRole = useSelector((state: RootState) =>
  //   currentMap ? selectUserRoleForMap(state, currentMap.uuid) : null,
  // );
  const userRole = useRoleWithOverride(currentMap?.uuid || '');
  const { canComment } = getPermissionsForRole(userRole);

  useEffect(() => {
    if (commentToEdit) {
      setCommentText(commentToEdit.text);
      setVideoUrl(commentToEdit.videoUrl || '');
      setShowVideoInput(!!commentToEdit.videoUrl);
    }
  }, [commentToEdit]);

  // Don't render the component if user can't comment
  if (!canComment) {
    return null;
  }

  const validateVideoUrl = (url: string): boolean => {
    if (!url) return true; // Empty URL is valid
    const supportedDomains = ['loom.com', 'youtube.com', 'vimeo.com'];
    try {
      const urlObj = new URL(url);
      return supportedDomains.some((domain) => urlObj.hostname.endsWith(domain));
    } catch {
      return false;
    }
  };

  const handleSave = () => {
    // Clear previous errors
    setVideoUrlError('');
    setCommentTextError('');

    if (!canComment) {
      console.warn('Comment save denied: user does not have comment permissions');
      return;
    }

    if (!objectiveUuid || !commentText.trim()) {
      setCommentTextError('Please enter a comment');
      return;
    }

    if (showVideoInput && videoUrl && !validateVideoUrl(videoUrl)) {
      setVideoUrlError('Please enter a valid URL from Loom, YouTube, or Vimeo');
      return;
    }

    const now = new Date().toISOString();

    if (commentToEdit) {
      const updatedComments =
        objective?.comments.map((comment) =>
          comment.uuid === commentToEdit.uuid
            ? {
                ...comment,
                text: commentText.trim(),
                videoUrl: showVideoInput && videoUrl ? videoUrl.trim() : null,
              }
            : comment,
        ) || [];

      dispatch(
        updateObjective({
          uuid: objectiveUuid,
          updates: {
            comments: updatedComments,
          },
        }),
      );
    } else {
      const uuid = crypto.randomUUID();
      const newComment: Comment1 = {
        uuid,
        shortId: getShortIdFromUuid(uuid),
        text: commentText.trim(),
        videoUrl: showVideoInput && videoUrl ? videoUrl.trim() : null,
        forIntervalEnding,
        createdDate: now,
        createdBy: userProfile?.userName || 'Unknown User',
      };

      dispatch(
        updateObjective({
          uuid: objectiveUuid,
          updates: {
            comments: [...(objective?.comments || []), newComment],
          },
        }),
      );
    }

    setCommentText('');
    setVideoUrl('');
    setShowVideoInput(false);
    setVideoUrlError('');
    onSave?.();
  };

  const handleCancel = () => {
    setCommentText('');
    setVideoUrl('');
    setShowVideoInput(false);
    setVideoUrlError('');
    onCancel?.();
  };

  return (
    <div className="mt-6 pb-6">
      <Field>
        <Textarea
          name="comment"
          rows={4}
          resizable={true}
          value={commentText}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
            setCommentText(e.target.value);
            setCommentTextError('');
          }}
        />
        {commentTextError && <div className="text-red-500 text-sm mt-1">{commentTextError}</div>}
      </Field>
      {showVideoInput && (
        <Field className="mt-2">
          <Input
            type="url"
            placeholder="Enter video URL (Loom, YouTube, or Vimeo)"
            value={videoUrl}
            onChange={(e) => {
              setVideoUrl(e.target.value);
              setVideoUrlError('');
            }}
          />
          {videoUrlError && <div className="text-red-500 text-sm mt-1">{videoUrlError}</div>}
        </Field>
      )}
      <div className="flex items-center gap-2 mt-2">
        <label className="flex items-center text-sm text-gray-600">
          <input
            type="checkbox"
            className="mr-2"
            checked={showVideoInput}
            onChange={(e) => setShowVideoInput(e.target.checked)}
          />
          Add video
        </label>
      </div>
      <div className="flex justify-end gap-2 mt-2">
        {commentToEdit && (
          <Button outline onClick={handleCancel}>
            Cancel
          </Button>
        )}
        <Button outline onClick={handleSave}>
          {commentToEdit ? 'Save' : 'Comment'}
        </Button>
      </div>
    </div>
  );
}
